import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MultiEmailField from '../components/MultiEmailField';
import MultiIbanField from '../components/MultiIbanField';
import { getBankNameFromIbans } from '../utils/bankMapping';
import { PageHeader } from '../components/Navigation';
import { getBackPath } from '../utils/navigation';
import '../InvoiceDetail.css';
import '../Clients.css';
import { apiFetch } from '../utils/api';

export default function ClientCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const backPath = getBackPath(location, '/clients');

  const breadcrumbs = [
    { label: 'Validare', to: '/validation-hub' },
    { label: 'Emitenți', to: '/clients' },
    { label: 'Creare Emitent', to: null }
  ];

  const [form, setForm] = useState({
    nume: '',
    cui: '',
    email: [],  // Changed to array for MultiEmailField
    nr_reg_com: '',
    iban: [],  // Changed to array for MultiIbanField
    numeBanca: '',  // Bank name - auto-filled from IBAN
    adresa: '',
    oras: '',
    judet: '',
    tara_cod: 'RO',
    tva: '21',
    last_invoice_number: '',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Auto-fill bank name when IBAN changes
  useEffect(() => {
    const detectedBank = getBankNameFromIbans(form.iban);
    if (detectedBank && detectedBank !== form.numeBanca) {
      setForm((f) => ({ ...f, numeBanca: detectedBank }));
    }
  }, [form.iban, form.numeBanca]);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  const errors = useMemo(() => {
    const e = {};
    if (!form.nume.trim()) e.nume = 'Denumirea este obligatorie';
    if (!form.cui.trim()) e.cui = 'CUI este obligatoriu';
    // Email validation - check if array has at least one valid email
    if (!Array.isArray(form.email) || form.email.length === 0) {
      e.email = 'Cel puțin un email este obligatoriu';
    }
    if (!form.nr_reg_com.trim()) e.nr_reg_com = 'Nr. Reg. Com. este obligatoriu';
    if (!form.adresa.trim()) e.adresa = 'Adresa este obligatorie';
    if (!form.oras.trim()) e.oras = 'Orașul este obligatoriu';
    if (!form.judet.trim()) e.judet = 'Județul este obligatoriu';
    if (!form.tara_cod.trim()) e.tara_cod = 'Țara este obligatorie';
    if (!(String(form.tva).trim()) || isNaN(Number(form.tva))) e.tva = 'TVA (procent) este obligatoriu';
    else {
      const v = Number(form.tva);
      if (v < 0 || v > 99) e.tva = 'TVA trebuie să fie între 0 și 99';
    }
    return e;
  }, [form]);

  const allValid = Object.keys(errors).length === 0;

  async function handleSave() {
    if (!allValid) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await apiFetch('/api/clients', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        let msg = 'Eroare la crearea emitentului';
        try { const p = await res.clone().json(); if (p?.msg) msg = p.msg; } catch (_) {}
        throw new Error(msg);
      }
      const json = await res.json();
      const id = json?.client?.id;
      if (id) navigate(`/clients/${id}`);
      else setMessage({ type: 'success', text: 'Emitent creat.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="invoice-detail">
      <PageHeader
        backTo={backPath}
        backLabel="Înapoi la Emitenți"
        title="Creare Emitent"
        subtitle="Completați toate câmpurile și salvați"
        breadcrumbs={breadcrumbs}
      />

      <main className="invoice-detail-main">
        <div className="invoice-detail-content">
          {message && (
            <div className={`validation-message ${message.type === 'error' ? 'validation-message-error' : 'validation-message-success'}`}>
              <p className="validation-message-text">{message.text}</p>
            </div>
          )}

          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">Date Emitent</h2>
              <p className="section-subtitle">Toate câmpurile sunt obligatorii</p>
            </div>
            <div className="section-content">
              <div className="form-grid">
                <div className="form-field">
                  <label className={`form-field-label ${errors.nume ? 'form-field-label-unknown' : ''}`}>Denumire *</label>
                  <input className={`form-field-input ${errors.nume ? 'form-field-input-error' : ''}`} value={form.nume} onChange={(e) => update('nume', e.target.value)} />
                  {errors.nume && <div className="form-field-error">{errors.nume}</div>}
                </div>

                <div className="form-field">
                  <label className={`form-field-label ${errors.cui ? 'form-field-label-unknown' : ''}`}>CUI *</label>
                  <input className={`form-field-input form-field-input-mono ${errors.cui ? 'form-field-input-error' : ''}`} value={form.cui} onChange={(e) => update('cui', e.target.value)} />
                  {errors.cui && <div className="form-field-error">{errors.cui}</div>}
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <MultiEmailField
                    emails={form.email}
                    onChange={(v) => update('email', v)}
                    label="Email *"
                    readOnly={false}
                  />
                  {errors.email && <div className="form-field-error">{errors.email}</div>}
                </div>

                <div className="form-field">
                  <label className={`form-field-label ${errors.nr_reg_com ? 'form-field-label-unknown' : ''}`}>Nr. Reg. Com. *</label>
                  <input className={`form-field-input form-field-input-mono ${errors.nr_reg_com ? 'form-field-input-error' : ''}`} value={form.nr_reg_com} onChange={(e) => update('nr_reg_com', e.target.value)} />
                  {errors.nr_reg_com && <div className="form-field-error">{errors.nr_reg_com}</div>}
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <MultiIbanField
                    ibans={form.iban}
                    onChange={(v) => update('iban', v)}
                    label="IBAN"
                    readOnly={false}
                  />
                </div>

                <div className="form-field">
                  <label className="form-field-label">Bancă</label>
                  <input
                    className="form-field-input"
                    value={form.numeBanca}
                    onChange={(e) => update('numeBanca', e.target.value)}
                    placeholder="Se completează automat din IBAN"
                  />
                </div>

                <div className="form-field">
                  <label className="form-field-label">Număr Ultima Factură</label>
                  <input
                    className="form-field-input form-field-input-mono"
                    value={form.last_invoice_number}
                    onChange={(e) => update('last_invoice_number', e.target.value)}
                    placeholder="ex: FAC-2024-001"
                  />
                </div>

                <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                  <label className={`form-field-label ${errors.adresa ? 'form-field-label-unknown' : ''}`}>Adresă *</label>
                  <input className={`form-field-input ${errors.adresa ? 'form-field-input-error' : ''}`} value={form.adresa} onChange={(e) => update('adresa', e.target.value)} />
                  {errors.adresa && <div className="form-field-error">{errors.adresa}</div>}
                </div>

                <div className="form-field">
                  <label className={`form-field-label ${errors.oras ? 'form-field-label-unknown' : ''}`}>Oraș *</label>
                  <input className={`form-field-input ${errors.oras ? 'form-field-input-error' : ''}`} value={form.oras} onChange={(e) => update('oras', e.target.value)} />
                  {errors.oras && <div className="form-field-error">{errors.oras}</div>}
                </div>

                <div className="form-field">
                  <label className={`form-field-label ${errors.judet ? 'form-field-label-unknown' : ''}`}>Județ *</label>
                  <input className={`form-field-input ${errors.judet ? 'form-field-input-error' : ''}`} value={form.judet} onChange={(e) => update('judet', e.target.value)} />
                  {errors.judet && <div className="form-field-error">{errors.judet}</div>}
                </div>

                <div className="form-field">
                  <label className={`form-field-label ${errors.tara_cod ? 'form-field-label-unknown' : ''}`}>Țara *</label>
                  <input className={`form-field-input ${errors.tara_cod ? 'form-field-input-error' : ''}`} value={form.tara_cod} onChange={(e) => update('tara_cod', e.target.value)} />
                  {errors.tara_cod && <div className="form-field-error">{errors.tara_cod}</div>}
                </div>

                <div className="form-field">
                  <label className={`form-field-label ${errors.tva ? 'form-field-label-unknown' : ''}`}>TVA Implicit (%) *</label>
                  <input className={`form-field-input ${errors.tva ? 'form-field-input-error' : ''}`} value={form.tva} onChange={(e) => update('tva', e.target.value)} />
                  {errors.tva && <div className="form-field-error">{errors.tva}</div>}
                </div>
              </div>
            </div>
          </div>

          <div className="section-card">
            <div className="section-content">
              <div className="actions-container">
                <div className="actions-info">
                  <h3>Acțiuni</h3>
                  <p>Salvarea este posibilă doar dacă toate câmpurile sunt completate</p>
                </div>
                <div className="actions-buttons">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!allValid || saving}
                    className={`action-btn ${(!allValid || saving) ? 'action-btn-disabled' : 'action-btn-save'}`}
                  >
                    Salvează
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
}
